// ============================================================
// 📁 آدرس این فایل در پروژه:
//    src/pages/warehouse/GoodsReceiptPage.tsx
//
// نسخه‌ی ۳ — دو مرحله‌ی جدا برای هر جنس:
//   ۱) توزیع بار: بار بین موترها تقسیم می‌شود (هر ردیف: گدام، واحد،
//      مقدار، شماره موتر، شماره بارنامه، توضیحات، ضمیمه — همه جدا)
//   ۲) رسید گدام: هر موتر تک‌تک تأیید می‌شود؛ کسری/اضافه (اگر بود)
//      با انتخاب واحد و مقدار همان کسری/اضافه ثبت می‌شود.
// همه‌ی فیلدهای ضمیمه کلید ضبط صدا دارند.
// ============================================================

import { useState, useEffect, useCallback } from "react";
import { useTheme, DARK, LIGHT } from "../../components/Layout";
import { goodsReceiptAPI, warehouseAPI, journalAPI, vendorsAPI } from "../../services/api";
import { generateReceiptsExcel, printReceiptsPdf } from "../../utils/goodsReceiptReport";
import SearchableSelect from "../../components/SearchableSelect";
import AttachmentField from "../../components/AttachmentField";
import PersianDatePicker, { todayGregorian, gregorianToJalaliStr as _g2j } from "../../utils/PersianDatePicker";

import { Search, Package, ArrowRight, Plus, Trash2, Clock, PackageX, CheckCircle2, Truck, FileCheck2, Sparkles, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Download, FileSpreadsheet, Printer, SlidersHorizontal, RotateCcw, Paperclip } from "lucide-react";

// همه‌ی اعداد سیستم انگلیسی باشند
const gregorianToJalaliStr = (d: string) => String(_g2j(d) ?? "").replace(/[۰-۹]/g, c => String("۰۱۲۳۴۵۶۷۸۹".indexOf(c))).replace(/[٠-٩]/g, c => String("٠١٢٣٤٥٦٧٨٩".indexOf(c)));

const fmt = (n: any) => new Intl.NumberFormat("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 3 }).format(Number(n) || 0);
const fmt2 = (n: any) => new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

const inputSt = (T: any): React.CSSProperties => ({
  width: "100%", padding: "10px 14px", borderRadius: 10,
  border: `1.5px solid ${T.border}`, background: T.input,
  color: T.text, fontSize: 13, fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
  boxSizing: "border-box", outline: "none",
});
const Lbl = ({ T, children }: any) => (
  <label style={{ display: "block", fontSize: 11.5, fontWeight: 700, color: T.subtext, marginBottom: 5 }}>{children}</label>
);
// واحدهای یک جنس بدون تکرار؛ اگر فقط یکی بود خودکار انتخاب می‌شود، اگر بیشتر بود کاربر باید انتخاب کند
const uniqUnits = (arr: any[] = []) => { const seen = new Set<string>(); return arr.filter(u => { const k = String(u.unit_id); if (seen.has(k)) return false; seen.add(k); return true; }); };
const unitLabel = (u: any) => u.name + (u.symbol ? ` (${u.symbol})` : "");
const uploadAttachment = (file: File) => goodsReceiptAPI.uploadAttachment(file);

declare const process: any;
const EMPTY_FILTERS = { keyword: "", product_id: "", vendor_id: "", purchase_id: "", warehouse_id: "", status: "", discrepancy: "", date_from: "", date_to: "", has_attachment: false };
const STATUS_OPTS = [
  { value: "", label: "همه وضعیت‌ها" }, { value: "planned", label: "در انتظار رسید" },
  { value: "received", label: "رسید شده" }, { value: "waste", label: "ضایعات" },
];
const DISC_OPTS = [
  { value: "", label: "همه (با/بدون کسری)" }, { value: "any", label: "فقط دارای کسری یا اضافه" },
  { value: "shortage", label: "فقط کسری" }, { value: "overage", label: "فقط اضافه" }, { value: "none", label: "بدون کسری و اضافه" },
];
const HIST_PER_PAGE = 10;

const statusInfo = (r: any) => r.type === "waste" ? { t: "ضایعات", c: "#9a3412" } : r.status === "received" ? { t: "رسید شده", c: "#10b981" } : { t: "در انتظار رسید", c: "#f59e0b" };

export default function GoodsReceiptPage() {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  const BORDER = dark ? "rgba(255,255,255,0.08)" : "#e9ecf2";
  const SURFACE = dark ? "rgba(255,255,255,0.025)" : "#ffffff";

  const [list, setList]           = useState<any[]>([]);
  const [loading, setLoading]     = useState(true);
  const [showCompleted, setShowCompleted] = useState(false);
  const [openPurchaseId, setOpenPurchaseId] = useState<number | null>(null);
  const [toast, setToast]         = useState({ text: "", ok: true });

  // فیلترها
  const [f, setF] = useState<any>({ ...EMPTY_FILTERS });
  const [showMore, setShowMore] = useState(false);
  const [kwDebounced, setKwDebounced] = useState("");
  const [products, setProducts] = useState<any[]>([]);
  const [vendors, setVendors]   = useState<any[]>([]);
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [allPurchases, setAllPurchases] = useState<any[]>([]);

  // تاریخچه
  const [hist, setHist] = useState<any[]>([]);
  const [histLoading, setHistLoading] = useState(true);
  const [histPage, setHistPage] = useState(1);
  const [histTotalPages, setHistTotalPages] = useState(1);
  const [histTotal, setHistTotal] = useState(0);
  const [histSum, setHistSum] = useState<any>(null);

  // دانلود
  const [showExport, setShowExport] = useState(false);
  const [expAll, setExpAll] = useState(true);
  const [expFrom, setExpFrom] = useState(todayGregorian());
  const [expTo, setExpTo] = useState(todayGregorian());
  const [exporting, setExporting] = useState("");

  const showToast = (text: string, ok = true) => { setToast({ text, ok }); setTimeout(() => setToast({ text: "", ok: true }), 3200); };
  const set = (patch: any) => setF((p: any) => ({ ...p, ...patch }));

  useEffect(() => {
    (async () => {
      try {
        const [p, v, w] = await Promise.all([warehouseAPI.productsList(), vendorsAPI.list(), warehouseAPI.warehousesList()]);
        if (p.success) setProducts(p.products || []);
        if (v.success) setVendors(v.vendors || []);
        if (w.success) setWarehouses(w.warehouses || []);
        const pp = await goodsReceiptAPI.pendingPurchasesList({});
        if (pp.success) setAllPurchases(pp.purchases || []);
      } catch {}
    })();
  }, []);

  useEffect(() => { const t = setTimeout(() => setKwDebounced(f.keyword), 350); return () => clearTimeout(t); }, [f.keyword]);

  const apiFilters = () => ({
    keyword: kwDebounced || undefined,
    product_id: f.product_id ? Number(f.product_id) : undefined,
    vendor_id: f.vendor_id ? Number(f.vendor_id) : undefined,
    purchase_id: f.purchase_id ? Number(f.purchase_id) : undefined,
    warehouse_id: f.warehouse_id ? Number(f.warehouse_id) : undefined,
    status: f.status || undefined, discrepancy: f.discrepancy || undefined,
    date_from: f.date_from || undefined, date_to: f.date_to || undefined,
    has_attachment: f.has_attachment ? 1 : undefined,
  });
  const filtersKey = JSON.stringify([kwDebounced, f.purchase_id, f.product_id, f.vendor_id, f.warehouse_id, f.status, f.discrepancy, f.date_from, f.date_to, f.has_attachment]);

  const loadPurchases = useCallback(async () => {
    setLoading(true);
    const a: any = apiFilters();
    const r = await goodsReceiptAPI.pendingPurchasesList({ keyword: a.keyword, product_id: a.product_id, vendor_id: a.vendor_id, date_from: a.date_from, date_to: a.date_to });
    if (r.success) setList(r.purchases || []);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);

  const loadHistory = useCallback(async (page: number) => {
    setHistLoading(true);
    const r = await goodsReceiptAPI.receiptsHistory({ ...apiFilters(), page, per_page: HIST_PER_PAGE });
    if (r.success) {
      setHist(r.rows || []); setHistTotal(r.total || 0); setHistTotalPages(r.total_pages || 1); setHistSum(r.summary || null);
    }
    setHistLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);

  useEffect(() => { setHistPage(1); loadPurchases(); loadHistory(1); }, [loadPurchases, loadHistory]);
  const goPage = (p: number) => { const np = Math.min(Math.max(1, p), histTotalPages); setHistPage(np); loadHistory(np); };

  const activeCount = [f.keyword, f.purchase_id, f.product_id, f.vendor_id, f.warehouse_id, f.status, f.discrepancy, f.date_from, f.date_to, f.has_attachment].filter(Boolean).length;
  const resetFilters = () => setF({ ...EMPTY_FILTERS });

  const setQuick = (type: string) => {
    const today = new Date(); const pad = (n: number) => String(n).padStart(2, "0");
    const g = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const to = todayGregorian(); let from = to;
    if (type === "week") { const d = new Date(today); d.setDate(d.getDate() - 6); from = g(d); }
    if (type === "month") from = g(new Date(today.getFullYear(), today.getMonth(), 1));
    if (type === "3month") { const d = new Date(today); d.setMonth(d.getMonth() - 3); from = g(d); }
    if (type === "year") from = g(new Date(today.getFullYear(), 0, 1));
    set({ date_from: from, date_to: to });
  };

  const productOpts = [{ value: "", label: "همه اجناس" }, ...products.map((p: any) => ({ value: String(p.id), label: p.name + (p.code ? ` (${p.code})` : "") }))];
  const vendorName = (v: any) => `${v.first_name || ""} ${v.last_name || ""} ${v.company_name || ""}`.trim() || v.name || `#${v.id}`;
  const vendorOpts = [{ value: "", label: "همه فروشنده‌ها" }, ...vendors.map((v: any) => ({ value: String(v.id), label: vendorName(v) }))];
  const purchaseOpts = [{ value: "", label: "همه خریدها" }, ...allPurchases.map((x: any) => ({ value: String(x.id), label: `${x.serial} — ${x.vendor_name}` }))];
  const whOpts = [{ value: "", label: "همه گدام‌ها" }, ...warehouses.map((w: any) => ({ value: String(w.id), label: w.name }))];

  // ── دانلود گزارش ──
  const filtersLabel = () => {
    const parts: string[] = [];
    if (f.keyword) parts.push(`جستجو: ${f.keyword}`);
    if (f.purchase_id) parts.push(`خرید: ${allPurchases.find((x: any) => String(x.id) === String(f.purchase_id))?.serial || ""}`);
    if (f.product_id) parts.push(`جنس: ${products.find((p: any) => String(p.id) === f.product_id)?.name || ""}`);
    if (f.vendor_id) parts.push(`فروشنده: ${vendorName(vendors.find((v: any) => String(v.id) === f.vendor_id) || {})}`);
    if (f.warehouse_id) parts.push(`گدام: ${warehouses.find((w: any) => String(w.id) === f.warehouse_id)?.name || ""}`);
    if (f.status) parts.push(`وضعیت: ${STATUS_OPTS.find(o => o.value === f.status)?.label}`);
    if (f.discrepancy) parts.push(`${DISC_OPTS.find(o => o.value === f.discrepancy)?.label}`);
    if (f.has_attachment) parts.push("دارای ضمیمه");
    return parts.join(" ، ");
  };

  const doExport = async (kind: "excel" | "pdf") => {
    if (!expAll && expFrom && expTo && expFrom > expTo) { showToast("تاریخ شروع نباید بعد از تاریخ پایان باشد", false); return; }
    setExporting(kind);
    try {
      const base: any = apiFilters();
      if (expAll) { base.date_from = undefined; base.date_to = undefined; }
      else { base.date_from = expFrom; base.date_to = expTo; }
      const r = await goodsReceiptAPI.receiptsHistory({ ...base, page: 1, per_page: 0 });
      if (!r.success) { showToast(r.message || "خطا در دریافت اطلاعات گزارش", false); setExporting(""); return; }
      const curPu = allPurchases.find((x: any) => String(x.id) === String(f.purchase_id));
      const meta = {
        title: curPu ? `گزارش توزیع بار و رسید — خرید ${curPu.serial} (${curPu.vendor_name})` : undefined,
        periodLabel: expAll ? "همه دوره‌ها" : `${gregorianToJalaliStr(expFrom)} تا ${gregorianToJalaliStr(expTo)}`,
        filtersLabel: filtersLabel(),
      };
      const rows = r.rows || [];
      if (kind === "excel") await generateReceiptsExcel(rows, meta);
      else if (!printReceiptsPdf(rows, meta)) showToast("مرورگر پنجره‌ی چاپ را مسدود کرد؛ اجازه‌ی پنجره‌ی بازشو را بدهید", false);
      setShowExport(false);
      if (openPurchaseId) set({ purchase_id: "" });
    } catch { showToast("خطا در ساخت گزارش", false); }
    setExporting("");
  };

  const chip = (active: boolean): React.CSSProperties => ({ padding: "6px 12px", borderRadius: 8, border: `1px solid ${active ? "#f59e0b" : BORDER}`, background: active ? "rgba(245,158,11,0.12)" : "transparent", color: active ? "#f59e0b" : T.subtext, cursor: "pointer", fontSize: 11.5, fontWeight: 700, fontFamily: "inherit" });

  const exportModal = (
    <>
      {showExport && (
        <Overlay onClose={() => { if (!exporting) { setShowExport(false); if (openPurchaseId) set({ purchase_id: "" }); } }} T={T} dark={dark} wide xl>
          <h3 style={{ margin: "0 0 4px", fontSize: 17, fontWeight: 900, color: T.text }}>📥 دانلود گزارش رسید جنس</h3>
          <p style={{ margin: "0 0 16px", fontSize: 12.5, color: T.subtext }}>دوره‌ی گزارش را انتخاب کنید (دوره‌ی انتخابی جایگزین فیلتر تاریخ بالای صفحه می‌شود). بقیه‌ی فیلترهای فعال در گزارش اعمال می‌شوند{activeCount > 0 ? ` (${activeCount} فیلتر فعال)` : ""}.</p>

          <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
            <button onClick={() => setExpAll(true)} style={{ ...chip(expAll), flex: 1, padding: "11px" }}>همه دوره‌ها</button>
            <button onClick={() => setExpAll(false)} style={{ ...chip(!expAll), flex: 1, padding: "11px" }}>انتخاب بازه‌ی زمانی</button>
          </div>
          {!expAll && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
              <div><Lbl T={T}>از تاریخ</Lbl><PersianDatePicker value={expFrom} onChange={setExpFrom} /></div>
              <div><Lbl T={T}>تا تاریخ</Lbl><PersianDatePicker value={expTo} onChange={setExpTo} /></div>
            </div>
          )}

          <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
            <button disabled={!!exporting} onClick={() => doExport("excel")} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "13px", borderRadius: 11, border: "none", cursor: exporting ? "wait" : "pointer", background: "linear-gradient(135deg,#10b981,#059669)", color: "#fff", fontSize: 13.5, fontWeight: 800, fontFamily: "inherit" }}>
              <FileSpreadsheet size={16} /> {exporting === "excel" ? "در حال ساخت..." : "فایل اکسل"}
            </button>
            <button disabled={!!exporting} onClick={() => doExport("pdf")} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "13px", borderRadius: 11, border: "none", cursor: exporting ? "wait" : "pointer", background: "linear-gradient(135deg,#ef4444,#dc2626)", color: "#fff", fontSize: 13.5, fontWeight: 800, fontFamily: "inherit" }}>
              <Printer size={16} /> {exporting === "pdf" ? "در حال ساخت..." : "فایل PDF"}
            </button>
          </div>
          <button disabled={!!exporting} onClick={() => setShowExport(false)} style={{ width: "100%", marginTop: 10, padding: "10px", borderRadius: 10, border: `1px solid ${T.cardBorder}`, background: "transparent", color: T.text, cursor: "pointer", fontSize: 13, fontFamily: "inherit" }}>انصراف</button>
          <p style={{ margin: "12px 0 0", fontSize: 11, color: T.subtext }}>برای PDF: در پنجره‌ی چاپ، مقصد را «Save as PDF» انتخاب کنید.</p>
        </Overlay>
      )}
    </>
  );

  if (openPurchaseId) {
    return (
      <>
        <Toast toast={toast} />
        <PurchaseDetail purchaseId={openPurchaseId} onBack={() => { setOpenPurchaseId(null); loadPurchases(); loadHistory(histPage); }} T={T} dark={dark} showToast={showToast}
          onExport={() => { set({ purchase_id: String(openPurchaseId) }); setExpAll(true); setShowExport(true); }} />
        {exportModal}
      </>
    );
  }

  const filtered = list.filter(p => showCompleted || !p.fully_received);
  const pagerBtn = (disabled: boolean): React.CSSProperties => ({
    width: 32, height: 32, borderRadius: 8, border: `1px solid ${BORDER}`, background: dark ? "rgba(255,255,255,0.03)" : "#fff",
    color: disabled ? T.subtext : T.text, cursor: disabled ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", opacity: disabled ? 0.4 : 1,
  });
  const th: React.CSSProperties = { padding: "11px 10px", fontSize: 11, fontWeight: 800, color: dark ? "#cbd5e1" : "#f1f5f9", background: dark ? "#0f172a" : "#1e293b", whiteSpace: "nowrap", textAlign: "center", position: "sticky", top: 0, zIndex: 2 };
  const td: React.CSSProperties = { padding: "10px 10px", fontSize: 12, color: T.text, borderBottom: `1px solid ${BORDER}`, textAlign: "center", whiteSpace: "nowrap" };

  const statCards = histSum ? [
    { l: "تعداد ردیف", v: fmt(histSum.c), c: "#7c3aed" },
    { l: "بار توزیع‌شده", v: fmt(histSum.planned_qty), c: "#2563eb" },
    { l: "رسیده به گدام", v: fmt(histSum.received_qty), c: "#10b981" },
    { l: "در انتظار رسید", v: fmt(histSum.waiting_qty), c: "#f59e0b" },
    { l: "جمع کسری", v: fmt(histSum.shortage_qty), c: "#dc2626" },
    { l: "جمع اضافه", v: fmt(histSum.overage_qty), c: "#16a34a" },
    { l: "ضایعات", v: fmt(histSum.waste_qty), c: "#9a3412" },
  ] : [];

  return (
    <div dir="rtl" style={{ fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }}>
      <Toast toast={toast} />

      <div style={{ marginBottom: 18, display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, color: "#f59e0b", fontWeight: 800, letterSpacing: "1px", marginBottom: 5 }}>📦 رسید جنس</div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 900, color: T.text }}>توزیع بار و رسید گدام</h2>
          <p style={{ margin: "4px 0 0", fontSize: 12.5, color: T.subtext }}>هر خرید اول بین موترها توزیع، بعد موتر به موتر به گدام رسید می‌شود</p>
        </div>
        <button onClick={() => setShowExport(true)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "11px 20px", borderRadius: 12, border: "none", cursor: "pointer", background: "linear-gradient(135deg,#f59e0b,#d97706)", color: "#fff", fontSize: 13, fontWeight: 800, fontFamily: "inherit", boxShadow: "0 8px 20px rgba(217,119,6,0.3)" }}>
          <Download size={16} /> دانلود گزارش
        </button>
      </div>

      {/* ───── نوار فیلتر حرفه‌ای ───── */}
      <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 16, padding: 14, marginBottom: 16, boxShadow: dark ? "none" : "0 1px 2px rgba(15,23,42,0.04)" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 10, alignItems: "end" }}>
          <div style={{ position: "relative" }}>
            <Lbl T={T}>جستجو</Lbl>
            <Search size={14} style={{ position: "absolute", right: 12, bottom: 12, color: T.subtext }} />
            <input value={f.keyword} onChange={e => set({ keyword: e.target.value })} placeholder="خرید، فروشنده، جنس، موتر، بارنامه، یادداشت..." style={{ ...inputSt(T), paddingRight: 34 }} />
          </div>
          <div><Lbl T={T}>شماره خرید</Lbl><SearchableSelect options={purchaseOpts} value={f.purchase_id} onChange={v => set({ purchase_id: v })} placeholder="همه خریدها" /></div>
          <div><Lbl T={T}>جنس</Lbl><SearchableSelect options={productOpts} value={f.product_id} onChange={v => set({ product_id: v })} placeholder="همه اجناس" /></div>
          <div><Lbl T={T}>گدام</Lbl><SearchableSelect options={whOpts} value={f.warehouse_id} onChange={v => set({ warehouse_id: v })} placeholder="همه گدام‌ها" /></div>
          <div><Lbl T={T}>فروشنده</Lbl><SearchableSelect options={vendorOpts} value={f.vendor_id} onChange={v => set({ vendor_id: v })} placeholder="همه فروشنده‌ها" /></div>
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center", flexWrap: "wrap" }}>
          <button onClick={() => setShowMore(s => !s)} style={{ ...chip(showMore), display: "flex", alignItems: "center", gap: 6 }}><SlidersHorizontal size={13} /> فیلترهای بیشتر (تاریخ، وضعیت، کسری...)</button>
          {activeCount > 0 && (
            <>
              <span style={{ fontSize: 11.5, color: "#f59e0b", fontWeight: 800, padding: "4px 10px", borderRadius: 20, background: "rgba(245,158,11,0.12)" }}>{activeCount} فیلتر فعال</span>
              <button onClick={resetFilters} style={{ ...chip(false), display: "flex", alignItems: "center", gap: 5 }}><RotateCcw size={12} /> پاک کردن همه</button>
            </>
          )}
        </div>

        {showMore && (
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px dashed ${BORDER}` }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 10, alignItems: "end" }}>
              <div><Lbl T={T}>از تاریخ</Lbl><PersianDatePicker value={f.date_from || ""} onChange={v => set({ date_from: v })} /></div>
              <div><Lbl T={T}>تا تاریخ</Lbl><PersianDatePicker value={f.date_to || ""} onChange={v => set({ date_to: v })} /></div>
              <div><Lbl T={T}>وضعیت</Lbl><SearchableSelect options={STATUS_OPTS} value={f.status} onChange={v => set({ status: v })} placeholder="همه وضعیت‌ها" /></div>
              <div><Lbl T={T}>کسری / اضافه</Lbl><SearchableSelect options={DISC_OPTS} value={f.discrepancy} onChange={v => set({ discrepancy: v })} placeholder="همه" /></div>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap", alignItems: "center" }}>
              <span style={{ fontSize: 11.5, color: T.subtext, fontWeight: 700 }}>بازه‌ی سریع:</span>
              {[["today", "امروز"], ["week", "۷ روز اخیر"], ["month", "این ماه"], ["3month", "۳ ماه اخیر"], ["year", "امسال"]].map(([k, l]) => (
                <button key={k} onClick={() => setQuick(k)} style={chip(false)}>{l}</button>
              ))}
              {(f.date_from || f.date_to) && <button onClick={() => set({ date_from: "", date_to: "" })} style={chip(false)}>✕ حذف بازه</button>}
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: T.subtext, cursor: "pointer", marginRight: "auto" }}>
                <input type="checkbox" checked={f.has_attachment} onChange={e => set({ has_attachment: e.target.checked })} /> فقط دارای ضمیمه / صدا
              </label>
            </div>
          </div>
        )}
      </div>

      {/* ───── خریدهای در انتظار توزیع/رسید ───── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: T.text, paddingRight: 9, borderRight: "3px solid #f59e0b" }}>خریدها (برای توزیع و رسید)</div>
        <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: T.subtext, cursor: "pointer" }}>
          <input type="checkbox" checked={showCompleted} onChange={e => setShowCompleted(e.target.checked)} />
          نمایش خریدهای کاملاً تکمیل‌شده هم
        </label>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 50, color: T.subtext }}>در حال بارگذاری...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: 50, color: T.subtext, background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)", borderRadius: 16, border: `1px dashed ${T.cardBorder}` }}>
          <Package size={40} style={{ opacity: 0.25, marginBottom: 12 }} />
          <div style={{ fontSize: 14 }}>هیچ خریدی یافت نشد</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {filtered.map(p => {
            const pct = p.total_ordered > 0 ? Math.min(100, (p.total_received / p.total_ordered) * 100) : 0;
            const color = p.fully_received ? "#10b981" : "#f59e0b";
            return (
              <div key={p.id} onClick={() => setOpenPurchaseId(p.id)} style={{
                padding: "18px 20px", borderRadius: 16, cursor: "pointer",
                background: dark ? "rgba(255,255,255,0.03)" : "#fff", border: `1px solid ${T.cardBorder}`,
                borderRight: `5px solid ${color}`, transition: "transform 0.15s, box-shadow 0.15s",
                boxShadow: dark ? "none" : "0 1px 3px rgba(0,0,0,0.04)",
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 8px 20px ${color}20`; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = dark ? "none" : "0 1px 3px rgba(0,0,0,0.04)"; }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                    <span style={{ padding: "4px 12px", borderRadius: 8, background: "rgba(245,158,11,0.12)", color: "#f59e0b", fontSize: 12.5, fontWeight: 800, fontFamily: "monospace" }}>{p.serial}</span>
                    <span style={{ fontSize: 14, fontWeight: 800, color: T.text }}>{p.vendor_name}</span>
                    {p.vendor_code && <span style={{ fontSize: 11, color: T.subtext, fontFamily: "monospace" }}>({p.vendor_code})</span>}
                    <span style={{ fontSize: 12, color: T.subtext }}>{gregorianToJalaliStr(p.date)}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    {p.fully_received ? (
                      <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 800, color: "#10b981" }}><CheckCircle2 size={14} /> تکمیل شده</span>
                    ) : (
                      <span style={{ fontSize: 12, fontWeight: 800, color: "#f59e0b" }}>باقیمانده: {fmt(p.total_remaining)}</span>
                    )}
                    <ArrowRight size={18} color={T.subtext} />
                  </div>
                </div>
                <div style={{ marginTop: 12, height: 7, borderRadius: 5, background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)", overflow: "hidden" }}>
                  <div style={{ width: `${pct}%`, height: "100%", borderRadius: 5, background: `linear-gradient(90deg, ${color}, ${color}cc)`, transition: "width 0.4s" }} />
                </div>
                <div style={{ marginTop: 7, fontSize: 11.5, color: T.subtext }}>
                  {p.items.length} قلم جنس · حساب‌شده {fmt(p.total_received)} از {fmt(p.total_ordered)} ({pct.toFixed(0)}٪)
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ───── جدول تاریخچه ───── */}
      <div style={{ marginTop: 34 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: T.text, marginBottom: 12, paddingRight: 9, borderRight: "3px solid #2563eb" }}>تاریخچه‌ی توزیع و رسید</div>

        {statCards.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))", gap: 10, marginBottom: 12 }}>
            {statCards.map((c, i) => (
              <div key={i} style={{ position: "relative", background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 12, padding: "10px 14px" }}>
                <span style={{ position: "absolute", top: 10, bottom: 10, right: 0, width: 4, borderRadius: 3, background: c.c }} />
                <div style={{ fontSize: 10.5, fontWeight: 700, color: T.subtext, marginBottom: 3 }}>{c.l}</div>
                <div style={{ fontSize: 16, fontWeight: 900, color: c.c }}>{c.v}</div>
              </div>
            ))}
          </div>
        )}

        <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 14, overflow: "hidden" }}>
          <div style={{ overflow: "auto", maxHeight: 460 }}>
            <table style={{ width: "100%", minWidth: 1250, borderCollapse: "separate", borderSpacing: 0 }}>
              <thead>
                <tr>
                  {["#", "تاریخ توزیع", "شماره خرید", "فروشنده", "جنس", "گدام", "مقدار", "موتر", "بارنامه", "وضعیت", "تاریخ رسید", "کسری / اضافه", "نهایی رسیده", "ضمیمه"].map(h => <th key={h} style={th}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {histLoading ? (
                  <tr><td colSpan={14} style={{ ...td, padding: 40, color: T.subtext }}>در حال بارگذاری...</td></tr>
                ) : hist.length === 0 ? (
                  <tr><td colSpan={14} style={{ ...td, padding: 50, color: T.subtext }}>هیچ ردیفی با این فیلترها یافت نشد</td></tr>
                ) : hist.map((r, i) => {
                  const st = statusInfo(r);
                  const link = r.receipt_attachment || r.attachment;
                  const isRecv = r.type === "receipt" && r.status === "received";
                  return (
                    <tr key={r.id} onClick={() => setOpenPurchaseId(Number(r.purchase_id))} style={{ cursor: "pointer", background: i % 2 === 0 ? "transparent" : (dark ? "rgba(255,255,255,0.02)" : "#f8fafc") }}
                      onMouseEnter={e => (e.currentTarget.style.background = dark ? "rgba(245,158,11,0.07)" : "rgba(245,158,11,0.06)")}
                      onMouseLeave={e => (e.currentTarget.style.background = i % 2 === 0 ? "transparent" : (dark ? "rgba(255,255,255,0.02)" : "#f8fafc"))}>
                      <td style={{ ...td, color: T.subtext }}>{(histPage - 1) * HIST_PER_PAGE + i + 1}</td>
                      <td style={td}>{gregorianToJalaliStr(r.date)}</td>
                      <td style={{ ...td, fontFamily: "monospace", fontWeight: 800, color: "#f59e0b" }}>{r.purchase_serial}</td>
                      <td style={td}>{r.vendor_name}</td>
                      <td style={{ ...td, fontWeight: 700 }}>{r.product_name}</td>
                      <td style={td}>{r.warehouse_name || "—"}</td>
                      <td style={{ ...td, fontWeight: 800 }}>{fmt(r.quantity_units)} {r.unit_name}</td>
                      <td style={td}>{r.truck_plate || "—"}</td>
                      <td style={td}>{r.waybill_number || "—"}</td>
                      <td style={td}><span style={{ padding: "3px 10px", borderRadius: 7, background: `${st.c}18`, color: st.c, fontWeight: 800, fontSize: 11 }}>{st.t}</span></td>
                      <td style={td}>{r.received_date ? gregorianToJalaliStr(r.received_date) : "—"}</td>
                      <td style={{ ...td, fontWeight: 800, color: r.discrepancy_direction === "shortage" ? "#dc2626" : r.discrepancy_direction === "overage" ? "#16a34a" : T.subtext }}>
                        {r.discrepancy_direction ? `${r.discrepancy_direction === "shortage" ? "کسری" : "اضافه"} ${fmt(Math.abs(r.discrepancy_qty_units))} ${r.discrepancy_unit_name || ""}` : "—"}
                      </td>
                      <td style={{ ...td, fontWeight: 800 }}>{r.type === "waste" ? `${fmt(r.base_quantity)} (ضایعات)` : isRecv ? fmt(r.base_quantity) : "—"}</td>
                      <td style={td} onClick={e => e.stopPropagation()}>
                        {link ? <a href={link.startsWith("http") ? link : (process.env.REACT_APP_API_URL ? new URL(process.env.REACT_APP_API_URL).origin : "") + link} target="_blank" rel="noreferrer" style={{ color: "#2563eb", display: "inline-flex", alignItems: "center", gap: 4, fontWeight: 700 }}><Paperclip size={13} /> مشاهده</a> : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* صفحه‌بندی */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, padding: "10px 16px", borderTop: `1px solid ${BORDER}` }}>
            <span style={{ fontSize: 12, color: T.subtext }}>
              مجموع <b style={{ color: T.text }}>{fmt(histTotal)}</b> ردیف — صفحه <b style={{ color: T.text }}>{histPage}</b> از <b style={{ color: T.text }}>{histTotalPages}</b> (هر صفحه {HIST_PER_PAGE} ردیف)
            </span>
            <div style={{ display: "flex", gap: 6 }}>
              <button disabled={histPage <= 1} onClick={() => goPage(1)} style={pagerBtn(histPage <= 1)} title="صفحه اول"><ChevronsRight size={15} /></button>
              <button disabled={histPage <= 1} onClick={() => goPage(histPage - 1)} style={pagerBtn(histPage <= 1)} title="صفحه قبلی"><ChevronRight size={15} /></button>
              <button disabled={histPage >= histTotalPages} onClick={() => goPage(histPage + 1)} style={pagerBtn(histPage >= histTotalPages)} title="صفحه بعدی"><ChevronLeft size={15} /></button>
              <button disabled={histPage >= histTotalPages} onClick={() => goPage(histTotalPages)} style={pagerBtn(histPage >= histTotalPages)} title="صفحه آخر"><ChevronsLeft size={15} /></button>
            </div>
          </div>
        </div>
      </div>

      {exportModal}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
function PurchaseDetail({ purchaseId, onBack, T, dark, showToast, onExport }: any) {
  const [detail, setDetail]   = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [openItemId, setOpenItemId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [r, w] = await Promise.all([goodsReceiptAPI.purchaseDetail(purchaseId), warehouseAPI.warehousesList()]);
    if (r.success) setDetail(r);
    if (w.success) setWarehouses(w.warehouses || []);
    setLoading(false);
  }, [purchaseId]);

  useEffect(() => { load(); }, [load]);

  if (loading || !detail) return <div style={{ textAlign: "center", padding: 70, color: T.subtext }}>در حال بارگذاری...</div>;
  const p = detail.purchase;

  return (
    <div dir="rtl" style={{ fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18, flexWrap: "wrap", gap: 10 }}>
        <button onClick={onBack} style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 18px", borderRadius: 10, border: `1px solid ${T.cardBorder}`, background: "transparent", color: T.subtext, cursor: "pointer", fontSize: 13, fontFamily: "inherit" }}>
          → بازگشت به لیست
        </button>
        {onExport && (
          <button onClick={onExport} style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 20px", borderRadius: 11, border: "none", cursor: "pointer", background: "linear-gradient(135deg,#f59e0b,#d97706)", color: "#fff", fontSize: 13, fontWeight: 800, fontFamily: "inherit", boxShadow: "0 8px 20px rgba(217,119,6,0.3)" }}>
            <Download size={15} /> دانلود گزارش همین خرید
          </button>
        )}
      </div>

      <div style={{ borderRadius: 18, padding: 22, marginBottom: 20, background: dark ? "linear-gradient(135deg,rgba(245,158,11,0.08),rgba(245,158,11,0.02))" : "linear-gradient(135deg,#fffbeb,#fff)", border: "1px solid rgba(245,158,11,0.25)" }}>
        <div style={{ display: "flex", gap: 32, flexWrap: "wrap" }}>
          <SummaryField T={T} label="شماره خرید" value={p.serial} mono />
          <SummaryField T={T} label="تاریخ خرید" value={gregorianToJalaliStr(p.date)} />
          <SummaryField T={T} label="فروشنده" value={p.vendor_name} />
          <SummaryField T={T} label="کد فروشنده" value={p.vendor_code || "—"} mono />
        </div>
        {detail.linked_purchases?.length > 0 && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px dashed ${T.cardBorder}` }}>
            <div style={{ fontSize: 11.5, fontWeight: 800, color: "#8b5cf6", marginBottom: 8 }}>🔗 خریدهای اضافه‌بار مرتبط با همین خرید</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {detail.linked_purchases.map((lp: any) => (
                <span key={lp.id} style={{ fontSize: 11.5, padding: "4px 12px", borderRadius: 8, background: "rgba(139,92,246,0.1)", color: "#8b5cf6", fontWeight: 700 }}>{lp.serial} — {fmt(lp.net_amount)} {lp.vendor_currency}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {detail.items.map((item: any) => (
          <ItemCard key={item.id} item={item} purchase={p} warehouses={warehouses}
            open={openItemId === item.id} onToggle={() => setOpenItemId(openItemId === item.id ? null : item.id)}
            onChanged={load} T={T} dark={dark} showToast={showToast} />
        ))}
      </div>
    </div>
  );
}

const SummaryField = ({ T, label, value, mono }: any) => (
  <div>
    <div style={{ fontSize: 11, color: T.subtext, marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 15, fontWeight: 800, color: T.text, fontFamily: mono ? "monospace" : "inherit" }}>{value}</div>
  </div>
);

// ────────────────────────────────────────────────────────────
type DistRow = { warehouse_id: string; unit_id: string; quantity_units: string; truck_plate: string; waybill_number: string; notes: string; attachment: string; attachment_file_name: string };
const emptyDistRow = (unitId = ""): DistRow => ({ warehouse_id: "", unit_id: unitId, quantity_units: "", truck_plate: "", waybill_number: "", notes: "", attachment: "", attachment_file_name: "" });

function ItemCard({ item, purchase, warehouses, open, onToggle, onChanged, T, dark, showToast }: any) {
  const [showDistForm, setShowDistForm] = useState(false);
  const [rows, setRows] = useState<DistRow[]>([]);
  const [distDate, setDistDate] = useState(todayGregorian());
  const [submittingDist, setSubmittingDist] = useState(false);
  const [showTruckCogsAsk, setShowTruckCogsAsk] = useState(false);
  const [confirmOver, setConfirmOver] = useState<null | { overBy: number }>(null);
  const [surplusPromptQty, setSurplusPromptQty] = useState<number | null>(null);
  const [showSurplusForm, setShowSurplusForm] = useState(false);
  const [surplus, setSurplus] = useState({ quantity: "", unit_price: "" });

  const [distRows, setDistRows] = useState<any[]>([]);
  const [rowsLoaded, setRowsLoaded] = useState(false);
  const [receivingRowId, setReceivingRowId] = useState<number | null>(null);
  const [showWasteConfirm, setShowWasteConfirm] = useState(false);
  const [wasteNotes, setWasteNotes] = useState("");
  const [wasteSubmitting, setWasteSubmitting] = useState(false);

  const pct = item.quantity > 0 ? Math.min(100, (item.received_base_qty / item.quantity) * 100) : 0;
  const isComplete = item.remaining_base_qty <= 0.0001;
  const itemUnits = uniqUnits(item.available_units);
  const singleUnit = itemUnits.length === 1 ? itemUnits[0] : null;
  const defaultUnitId = singleUnit ? String(singleUnit.unit_id) : "";
  const unitOptions = itemUnits.map((u: any) => ({ value: String(u.unit_id), label: unitLabel(u) }));
  const whOptions = warehouses.map((w: any) => ({ value: String(w.id), label: w.name }));
  const wasteValue = item.remaining_base_qty * (Number(item.unit_price) || 0);

  const loadRows = async () => {
    const r = await goodsReceiptAPI.itemDistributionRows(item.id);
    if (r.success) setDistRows(r.rows || []);
    setRowsLoaded(true);
  };
  const toggleOpen = () => { onToggle(); if (!rowsLoaded) loadRows(); };

  const rowExpectedBase = (row: DistRow) => {
    const unit = itemUnits.find((u: any) => String(u.unit_id) === row.unit_id);
    return unit ? (Number(row.quantity_units) || 0) * unit.conversion_factor : 0;
  };
  const batchTotal = rows.reduce((s, r) => s + rowExpectedBase(r), 0);

  const startDistForm = () => { setRows([emptyDistRow(defaultUnitId)]); setDistDate(todayGregorian()); setShowDistForm(true); };
  const addRow = () => setRows(r => [...r, emptyDistRow(defaultUnitId)]);
  const removeRow = (i: number) => setRows(r => r.filter((_, idx) => idx !== i));
  const updateRow = (i: number, patch: Partial<DistRow>) => setRows(r => r.map((row, idx) => idx === i ? { ...row, ...patch } : row));

  const doDistribute = async (createTruckCogs: boolean) => {
    setSubmittingDist(true);
    const overBy = (item.received_base_qty + batchTotal) - item.quantity;
    const res = await goodsReceiptAPI.distributeBatch({
      purchase_item_id: item.id, date: distDate, create_truck_cogs: createTruckCogs,
      rows: rows.map(r => ({ warehouse_id: parseInt(r.warehouse_id), unit_id: parseInt(r.unit_id), quantity_units: Number(r.quantity_units), truck_plate: r.truck_plate, waybill_number: r.waybill_number, notes: r.notes, attachment: r.attachment })),
    });
    setSubmittingDist(false);
    setShowTruckCogsAsk(false);
    setConfirmOver(null);
    if (res.success) {
      showToast("توزیع بار ثبت شد");
      setShowDistForm(false);
      onChanged(); loadRows();
      if (overBy > 0.0001) setSurplusPromptQty(overBy);
    } else showToast(res.message || "خطا در ثبت توزیع", false);
  };

  const proceedAfterOverCheck = () => {
    const hasAnyTruck = rows.some(r => r.truck_plate.trim() !== "");
    if (hasAnyTruck) setShowTruckCogsAsk(true);
    else doDistribute(false);
  };

  const handleDistSubmitClick = () => {
    if (rows.some(r => !r.unit_id)) { showToast("برای این جنس چند واحد تعریف شده؛ واحد همه‌ی ردیف‌ها را انتخاب کنید", false); return; }
    if (rows.some(r => !r.warehouse_id || !r.quantity_units)) { showToast("گدام، واحد و مقدار همه‌ی ردیف‌ها الزامی است", false); return; }
    const overBy = (item.received_base_qty + batchTotal) - item.quantity;
    if (overBy > 0.0001) { setConfirmOver({ overBy }); return; }
    proceedAfterOverCheck();
  };

  const saveSurplusPurchase = async () => {
    if (!surplus.quantity || !surplus.unit_price) { showToast("مقدار و قیمت الزامی است", false); return; }
    const qty = Number(surplus.quantity), price = Number(surplus.unit_price);
    const res = await journalAPI.addPurchase({
      date: todayGregorian(), vendor_id: purchase.vendor_id, vendor_name: purchase.vendor_name, purchase_type: "domestic",
      items: [{ product_id: item.product_id, product_name: item.product_name, unit: item.unit, quantity: qty, warehouse_id: null, warehouse_name: "", unit_price: price, total_price: qty * price, item_type: "product" }],
      notes: `اضافه‌بار مربوط به خرید ${purchase.serial}`, attachment: "", status: "confirmed", linked_purchase_id: purchase.id,
    });
    if (res.success) {
      showToast(`خرید اضافه‌بار ${res.serial} ثبت شد`);
      setShowSurplusForm(false); setSurplusPromptQty(null); setSurplus({ quantity: "", unit_price: "" });
      onChanged();
    } else showToast(res.message || "خطا", false);
  };

  const handleDeleteRow = async (id: number) => {
    if (!window.confirm("این ردیف حذف شود؟ (اگر رسید شده بود، موجودی گدام برمی‌گردد)")) return;
    const r = await goodsReceiptAPI.deleteDistributionRow(id);
    if (r.success) { showToast("حذف شد"); loadRows(); onChanged(); }
    else showToast(r.message || "خطا", false);
  };

  const doMarkWaste = async () => {
    setWasteSubmitting(true);
    const res = await goodsReceiptAPI.markWaste({ purchase_item_id: item.id, date: todayGregorian(), notes: wasteNotes });
    setWasteSubmitting(false);
    if (res.success) {
      showToast(`${fmt(res.waste_quantity)} به ارزش ${fmt2(res.waste_value)} ${res.currency} به‌عنوان ضایعات ثبت شد`);
      setShowWasteConfirm(false); setWasteNotes(""); onChanged(); loadRows();
    } else showToast(res.message || "خطا", false);
  };

  const plannedRows = distRows.filter(r => r.status === "planned");
  const receivedOrWaste = distRows.filter(r => r.status === "received");

  return (
    <div style={{ borderRadius: 16, border: `1px solid ${T.cardBorder}`, background: dark ? "rgba(255,255,255,0.025)" : "#fff", overflow: "hidden", boxShadow: dark ? "none" : "0 1px 3px rgba(0,0,0,0.03)" }}>
      <div onClick={toggleOpen} style={{ padding: "16px 20px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ width: 40, height: 40, borderRadius: 11, background: isComplete ? "rgba(16,185,129,0.12)" : "rgba(245,158,11,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            {isComplete ? <CheckCircle2 size={19} color="#10b981" /> : <Package size={19} color="#f59e0b" />}
          </div>
          <div>
            <div style={{ fontSize: 14.5, fontWeight: 800, color: T.text, display: "flex", alignItems: "center", gap: 8 }}>
              {item.product_name}
              {item.has_waste && <span style={{ fontSize: 10, fontWeight: 700, color: "#ef4444", background: "rgba(239,68,68,0.1)", padding: "2px 8px", borderRadius: 6 }}>شامل ضایعات</span>}
            </div>
            <div style={{ fontSize: 11.5, color: T.subtext, marginTop: 2 }}>سفارش: {fmt(item.quantity)} {item.unit} — حساب‌شده: <span style={{ color: "#10b981", fontWeight: 700 }}>{fmt(item.received_base_qty)}</span> — باقیمانده: <span style={{ color: isComplete ? "#10b981" : "#f59e0b", fontWeight: 700 }}>{fmt(item.remaining_base_qty)}</span></div>
          </div>
        </div>
        <div style={{ width: 150, height: 7, borderRadius: 5, background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)", overflow: "hidden" }}>
          <div style={{ width: `${pct}%`, height: "100%", borderRadius: 5, background: isComplete ? "#10b981" : "linear-gradient(90deg,#f59e0b,#f59e0bcc)" }} />
        </div>
      </div>

      {open && (
        <div style={{ padding: "0 20px 20px", borderTop: `1px solid ${T.cardBorder}` }}>

          {!showDistForm ? (
            <div style={{ marginTop: 16, display: "flex", gap: 10, flexWrap: "wrap" }}>
              {!isComplete && (
                <button onClick={startDistForm} style={{ display: "flex", alignItems: "center", gap: 7, padding: "10px 20px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#3b82f6,#2563eb)", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit", boxShadow: "0 3px 10px rgba(59,130,246,0.3)" }}>
                  <Truck size={15} /> توزیع بار جدید
                </button>
              )}
              {!isComplete && (
                <button onClick={() => setShowWasteConfirm(true)} style={{ display: "flex", alignItems: "center", gap: 7, padding: "10px 20px", borderRadius: 10, border: "1.5px solid rgba(239,68,68,0.35)", background: "transparent", color: "#ef4444", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit" }}>
                  <PackageX size={15} /> ثبت ضایعات برای باقیمانده
                </button>
              )}
            </div>
          ) : (
            <div style={{ marginTop: 16, padding: 18, borderRadius: 14, background: dark ? "rgba(59,130,246,0.05)" : "#eff6ff", border: "1px solid rgba(59,130,246,0.2)" }}>
              <div style={{ marginBottom: 14 }}>
                <Lbl T={T}>تاریخ توزیع</Lbl>
                <div style={{ maxWidth: 220 }}><PersianDatePicker value={distDate} onChange={setDistDate} /></div>
              </div>

              {rows.map((row, i) => {
                const expected = rowExpectedBase(row);
                return (
                  <div key={i} style={{ marginBottom: 12, padding: 14, borderRadius: 12, background: dark ? "rgba(255,255,255,0.02)" : "#fff", border: `1px solid ${T.border}` }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                      <span style={{ fontSize: 12, fontWeight: 800, color: "#3b82f6" }}>موتر {i + 1}</span>
                      {rows.length > 1 && (
                        <button onClick={() => removeRow(i)} style={{ width: 26, height: 26, borderRadius: 7, border: "none", background: "rgba(239,68,68,0.12)", color: "#ef4444", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><Trash2 size={12} /></button>
                      )}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
                      <div><Lbl T={T}>گدام مقصد *</Lbl><SearchableSelect options={whOptions} value={row.warehouse_id} onChange={v => updateRow(i, { warehouse_id: v })} placeholder="انتخاب گدام" /></div>
                      <div><Lbl T={T}>واحد {!singleUnit && "*"}</Lbl>{singleUnit
                        ? <div style={{ ...inputSt(T), display: "flex", alignItems: "center", fontWeight: 800, background: dark ? "rgba(16,185,129,0.08)" : "rgba(16,185,129,0.07)", borderColor: "rgba(16,185,129,0.35)", color: "#10b981" }}>{unitLabel(singleUnit)}</div>
                        : <SearchableSelect options={unitOptions} value={row.unit_id} onChange={v => updateRow(i, { unit_id: v })} placeholder="انتخاب واحد (الزامی)" />}</div>
                      <div>
                        <Lbl T={T}>تعداد / مقدار</Lbl>
                        <input type="number" value={row.quantity_units} onChange={e => updateRow(i, { quantity_units: e.target.value })} placeholder="0" style={inputSt(T)} />
                        {row.unit_id && row.quantity_units && <div style={{ fontSize: 10.5, color: "#10b981", marginTop: 3 }}>= {fmt(expected)}</div>}
                      </div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                      <div><Lbl T={T}>🚚 شماره موتر</Lbl><input value={row.truck_plate} onChange={e => updateRow(i, { truck_plate: e.target.value })} placeholder="مثلاً: 12345" style={{ ...inputSt(T), direction: "ltr" as const }} /></div>
                      <div><Lbl T={T}>📄 شماره بارنامه</Lbl><input value={row.waybill_number} onChange={e => updateRow(i, { waybill_number: e.target.value })} placeholder="شماره بارنامه..." style={{ ...inputSt(T), direction: "ltr" as const }} /></div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      <div><Lbl T={T}>توضیحات</Lbl><input value={row.notes} onChange={e => updateRow(i, { notes: e.target.value })} placeholder="مثلاً: نام راننده..." style={inputSt(T)} /></div>
                      <div>
                        <Lbl T={T}>ضمیمه</Lbl>
                        <AttachmentField value={row.attachment} fileName={row.attachment_file_name} onChange={(path, name) => updateRow(i, { attachment: path, attachment_file_name: name })} uploadFn={uploadAttachment} T={T} dark={dark} />
                      </div>
                    </div>
                  </div>
                );
              })}

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4, marginBottom: 14 }}>
                <button onClick={addRow} style={{ display: "flex", alignItems: "center", gap: 5, padding: "8px 16px", borderRadius: 9, border: "none", background: "rgba(59,130,246,0.12)", color: "#3b82f6", cursor: "pointer", fontSize: 12.5, fontWeight: 700, fontFamily: "inherit" }}>
                  <Plus size={13} /> افزودن موتر دیگر
                </button>
                <span style={{ fontSize: 12.5, color: T.subtext }}>مجموع این توزیع: <strong style={{ color: "#3b82f6" }}>{fmt(batchTotal)}</strong></span>
              </div>

              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button onClick={() => setShowDistForm(false)} style={{ padding: "9px 18px", borderRadius: 9, border: `1px solid ${T.cardBorder}`, background: "transparent", color: T.text, cursor: "pointer", fontSize: 13, fontFamily: "inherit" }}>انصراف</button>
                <button onClick={handleDistSubmitClick} disabled={submittingDist} style={{ padding: "9px 22px", borderRadius: 9, border: "none", background: submittingDist ? "#94a3b8" : "linear-gradient(135deg,#3b82f6,#2563eb)", color: "#fff", cursor: submittingDist ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit" }}>
                  {submittingDist ? "در حال ثبت..." : "ثبت توزیع بار"}
                </button>
              </div>
            </div>
          )}

          {/* ردیف‌های در انتظار رسید */}
          {plannedRows.length > 0 && (
            <div style={{ marginTop: 18 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: "#f59e0b", marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                <Truck size={13} /> در انتظار رسید گدام ({plannedRows.length})
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {plannedRows.map((r: any) => (
                  <div key={r.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 14px", borderRadius: 10, background: dark ? "rgba(245,158,11,0.06)" : "#fffbeb", border: "1px solid rgba(245,158,11,0.2)", flexWrap: "wrap", gap: 10 }}>
                    <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap", fontSize: 12 }}>
                      <span style={{ fontWeight: 800, color: T.text }}>{fmt(r.quantity_units)} {r.unit_name}</span>
                      <span style={{ color: T.subtext }}>🏭 {r.warehouse_name}</span>
                      {r.truck_plate && <span style={{ color: "#3b82f6", fontFamily: "monospace" }}>🚚 {r.truck_plate}</span>}
                      {r.waybill_number && <span style={{ color: "#0ea5e9", fontFamily: "monospace" }}>📄 {r.waybill_number}</span>}
                      {r.notes && <span style={{ color: T.subtext, fontStyle: "italic" }}>📝 {r.notes}</span>}
                      {r.attachment && <a href={r.attachment} target="_blank" rel="noreferrer" style={{ color: "#3b82f6" }}>ضمیمه</a>}
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => setReceivingRowId(r.id)} style={{ display: "flex", alignItems: "center", gap: 5, padding: "7px 16px", borderRadius: 8, border: "none", background: "linear-gradient(135deg,#10b981,#059669)", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: "inherit" }}>
                        <FileCheck2 size={12} /> تأیید رسید
                      </button>
                      <button onClick={() => handleDeleteRow(r.id)} style={{ width: 28, height: 28, borderRadius: 7, border: "none", background: "rgba(239,68,68,0.12)", color: "#ef4444", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><Trash2 size={12} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* تاریخچه (رسیدشده + ضایعات) */}
          <div style={{ marginTop: 18 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: T.subtext, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
              <Clock size={13} /> تاریخچه ({receivedOrWaste.length})
            </div>
            {receivedOrWaste.length === 0 ? (
              <div style={{ fontSize: 12, color: T.subtext, padding: "10px 0" }}>هنوز چیزی رسید نشده</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                {receivedOrWaste.map((h: any) => (
                  <div key={h.id} style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", borderRadius: 10, fontSize: 12, flexWrap: "wrap", gap: 8,
                    background: h.type === "waste" ? (dark ? "rgba(239,68,68,0.06)" : "#fef2f2") : (dark ? "rgba(255,255,255,0.03)" : "#f8fafc"),
                    border: h.type === "waste" ? "1px solid rgba(239,68,68,0.2)" : "none",
                  }}>
                    <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
                      {h.type === "waste" ? (
                        <span style={{ display: "flex", alignItems: "center", gap: 5, color: "#ef4444", fontWeight: 800 }}><PackageX size={13} /> ضایعات</span>
                      ) : (
                        <span style={{ color: T.subtext }}>{gregorianToJalaliStr(h.received_date || h.date)}</span>
                      )}
                      <span style={{ fontWeight: 700, color: T.text }}>{fmt(h.quantity_units)} {h.unit_name}</span>
                      <span style={{ color: "#10b981" }}>= {fmt(h.base_quantity)}</span>
                      {h.discrepancy_base_qty != null && Number(h.discrepancy_base_qty) !== 0 && (
                        <span style={{ color: h.discrepancy_direction === "shortage" ? "#ef4444" : "#10b981", fontWeight: 700 }}>
                          {h.discrepancy_direction === "shortage" ? "⚠ کسری" : "✓ اضافه"}: {fmt(Math.abs(h.discrepancy_qty_units))} {h.discrepancy_unit_name}
                        </span>
                      )}
                      {h.type === "waste" && h.waste_value != null && <span style={{ color: "#ef4444", fontWeight: 700 }}>ارزش: {fmt2(h.waste_value)}</span>}
                      {h.warehouse_name && h.type !== "waste" && <span style={{ color: T.subtext }}>🏭 {h.warehouse_name}</span>}
                      {h.truck_plate && <span style={{ color: "#3b82f6", fontFamily: "monospace" }}>🚚 {h.truck_plate}</span>}
                      {h.waybill_number && <span style={{ color: "#0ea5e9", fontFamily: "monospace" }}>📄 {h.waybill_number}</span>}
                      {h.notes && <span style={{ color: T.subtext, fontStyle: "italic" }}>📝 {h.notes}</span>}
                      {h.attachment && <a href={h.attachment} target="_blank" rel="noreferrer" style={{ color: "#3b82f6" }}>ضمیمه توزیع</a>}
                      {h.receipt_notes && <span style={{ color: T.subtext, fontStyle: "italic" }}>📝 رسید: {h.receipt_notes}</span>}
                      {h.receipt_attachment && <a href={h.receipt_attachment} target="_blank" rel="noreferrer" style={{ color: "#10b981" }}>ضمیمه رسید</a>}
                    </div>
                    <button onClick={() => handleDeleteRow(h.id)} style={{ width: 25, height: 25, borderRadius: 7, border: "none", background: "rgba(239,68,68,0.12)", color: "#ef4444", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Trash2 size={11} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* هشدار: مقدار توزیع از باقیمانده بیشتر است */}
      {confirmOver && (
        <Overlay onClose={() => setConfirmOver(null)} T={T} dark={dark}>
          <div style={{ width: 52, height: 52, borderRadius: 15, background: "rgba(239,68,68,0.12)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
            <PackageX size={24} color="#ef4444" />
          </div>
          <div style={{ fontSize: 14, color: T.text, marginBottom: 22, lineHeight: 1.9 }}>
            این مقدار <strong style={{ color: "#ef4444" }}>{fmt(confirmOver.overBy)}</strong> بیشتر از باقیمانده‌ی این آیتم است.<br />
            آیا مطمئنید می‌خواهید ثبت شود؟
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
            <button onClick={() => setConfirmOver(null)} style={{ padding: "10px 22px", borderRadius: 10, border: `1px solid ${T.cardBorder}`, background: "transparent", color: T.text, cursor: "pointer", fontSize: 13, fontFamily: "inherit" }}>خیر</button>
            <button onClick={() => { setConfirmOver(null); proceedAfterOverCheck(); }} style={{ padding: "10px 22px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#ef4444,#dc2626)", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit" }}>بله، ثبت شود</button>
          </div>
        </Overlay>
      )}

      {/* پرسش: خرید اضافه‌بار ثبت شود؟ */}
      {surplusPromptQty !== null && !showSurplusForm && (
        <Overlay onClose={() => setSurplusPromptQty(null)} T={T} dark={dark}>
          <div style={{ fontSize: 14, color: T.text, marginBottom: 22, lineHeight: 1.9 }}>
            مقدار <strong style={{ color: "#8b5cf6" }}>{fmt(surplusPromptQty)}</strong> بیشتر از خرید اصلی ثبت شد.<br />
            آیا می‌خواهید برای آن یک «خرید جدید» (اضافه‌بار) مرتبط با همین خرید ثبت شود؟
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
            <button onClick={() => setSurplusPromptQty(null)} style={{ padding: "10px 22px", borderRadius: 10, border: `1px solid ${T.cardBorder}`, background: "transparent", color: T.text, cursor: "pointer", fontSize: 13, fontFamily: "inherit" }}>خیر</button>
            <button onClick={() => { setSurplus({ quantity: String(surplusPromptQty), unit_price: String(item.unit_price || "") }); setShowSurplusForm(true); }} style={{ padding: "10px 22px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#8b5cf6,#7c3aed)", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit" }}>بله</button>
          </div>
        </Overlay>
      )}

      {showSurplusForm && (
        <Overlay onClose={() => { setShowSurplusForm(false); setSurplusPromptQty(null); }} T={T} dark={dark} wide>
          <div style={{ fontSize: 14, fontWeight: 800, color: T.text, marginBottom: 16 }}>خرید اضافه‌بار — مرتبط با {purchase.serial}</div>
          <div style={{ fontSize: 12, color: T.subtext, marginBottom: 14 }}>فروشنده: {purchase.vendor_name} — جنس: {item.product_name}</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 18 }}>
            <div><Lbl T={T}>مقدار (واحد پایه)</Lbl><input type="number" value={surplus.quantity} onChange={e => setSurplus(s => ({ ...s, quantity: e.target.value }))} style={inputSt(T)} /></div>
            <div><Lbl T={T}>قیمت واحد</Lbl><input type="number" value={surplus.unit_price} onChange={e => setSurplus(s => ({ ...s, unit_price: e.target.value }))} style={inputSt(T)} /></div>
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button onClick={() => { setShowSurplusForm(false); setSurplusPromptQty(null); }} style={{ padding: "9px 18px", borderRadius: 9, border: `1px solid ${T.cardBorder}`, background: "transparent", color: T.text, cursor: "pointer", fontSize: 13, fontFamily: "inherit" }}>انصراف</button>
            <button onClick={saveSurplusPurchase} style={{ padding: "9px 22px", borderRadius: 9, border: "none", background: "linear-gradient(135deg,#10b981,#059669)", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit" }}>ثبت خرید اضافه‌بار</button>
          </div>
        </Overlay>
      )}

      {/* دیالوگ لوکس: کرایه موتر */}
      {showTruckCogsAsk && (
        <Overlay onClose={() => { setShowTruckCogsAsk(false); }} T={T} dark={dark} wide>
          <div style={{ textAlign: "center" as const }}>
            <div style={{ width: 56, height: 56, borderRadius: 16, background: "linear-gradient(135deg,#f59e0b,#d97706)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 18px", boxShadow: "0 8px 20px rgba(245,158,11,0.35)" }}>
              <Truck size={26} color="#fff" />
            </div>
            <div style={{ fontSize: 16, fontWeight: 900, color: T.text, marginBottom: 8 }}>تراکنش کرایه موتر</div>
            <div style={{ fontSize: 13.5, color: T.subtext, lineHeight: 1.9, marginBottom: 24 }}>
              برای موترهایی که شماره‌ی پلیت وارد کرده‌اید، یک تراکنش هزینه‌ی اجناس (COGS) در حالت «در انتظار» ساخته شود؟
            </div>
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              <button onClick={() => doDistribute(false)} style={{ padding: "11px 26px", borderRadius: 12, border: `1.5px solid ${T.cardBorder}`, background: "transparent", color: T.text, cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit" }}>خیر</button>
              <button onClick={() => doDistribute(true)} style={{ padding: "11px 30px", borderRadius: 12, border: "none", background: "linear-gradient(135deg,#f59e0b,#d97706)", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 800, fontFamily: "inherit", boxShadow: "0 4px 14px rgba(245,158,11,0.35)", display: "flex", alignItems: "center", gap: 7 }}>
                <Sparkles size={14} /> بله، ثبت شود
              </button>
            </div>
          </div>
        </Overlay>
      )}

      {receivingRowId && (
        <ReceiveRowModal
          row={plannedRows.find((r: any) => r.id === receivingRowId)}
          item={item}
          onClose={() => setReceivingRowId(null)}
          onDone={(overageBase?: number) => { setReceivingRowId(null); loadRows(); onChanged(); if (overageBase && overageBase > 0.0001) setSurplusPromptQty(overageBase); }}
          T={T} dark={dark} showToast={showToast}
        />
      )}

      {showWasteConfirm && (
        <Overlay onClose={() => setShowWasteConfirm(false)} T={T} dark={dark} wide>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            <PackageX size={24} color="#ef4444" />
            <div style={{ fontSize: 15, fontWeight: 800, color: T.text }}>ثبت ضایعات</div>
          </div>
          <div style={{ fontSize: 13, color: T.text, lineHeight: 1.9, marginBottom: 16, background: dark ? "rgba(239,68,68,0.06)" : "#fef2f2", padding: 14, borderRadius: 10 }}>
            باقیمانده‌ی این آیتم (<strong>{fmt(item.remaining_base_qty)} {item.unit}</strong>) به‌عنوان ضایعات بسته می‌شود.
            ارزش آن به قیمت خرید: <div style={{ marginTop: 8, fontSize: 16, fontWeight: 900, color: "#ef4444" }}>{fmt2(wasteValue)} {purchase?.vendor_currency || ""}</div>
          </div>
          <div style={{ marginBottom: 18 }}>
            <Lbl T={T}>توضیحات (اختیاری)</Lbl>
            <input value={wasteNotes} onChange={e => setWasteNotes(e.target.value)} placeholder="مثلاً: علت ضایعات..." style={inputSt(T)} />
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button onClick={() => setShowWasteConfirm(false)} style={{ padding: "9px 18px", borderRadius: 9, border: `1px solid ${T.cardBorder}`, background: "transparent", color: T.text, cursor: "pointer", fontSize: 13, fontFamily: "inherit" }}>انصراف</button>
            <button onClick={doMarkWaste} disabled={wasteSubmitting} style={{ padding: "9px 22px", borderRadius: 9, border: "none", background: wasteSubmitting ? "#94a3b8" : "linear-gradient(135deg,#ef4444,#dc2626)", color: "#fff", cursor: wasteSubmitting ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit" }}>
              {wasteSubmitting ? "در حال ثبت..." : "تأیید و ثبت ضایعات"}
            </button>
          </div>
        </Overlay>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// مودال «تأیید رسید» یک ردیف برنامه‌ریزی‌شده
// ────────────────────────────────────────────────────────────
function ReceiveRowModal({ row, item, onClose, onDone, T, dark, showToast }: any) {
  const [receivedDate, setReceivedDate] = useState(todayGregorian());
  const [hasDiscrepancy, setHasDiscrepancy] = useState(false);
  const [direction, setDirection] = useState<"shortage" | "overage">("shortage");
  const [discUnitId, setDiscUnitId] = useState("");
  const [discQty, setDiscQty] = useState("");
  const [receiptNotes, setReceiptNotes] = useState("");
  const [receiptAttachment, setReceiptAttachment] = useState({ path: "", name: "" });
  const [submitting, setSubmitting] = useState(false);

  if (!row) return null;
  const itemUnits = uniqUnits(item.available_units);
  const singleUnit = itemUnits.length === 1 ? itemUnits[0] : null;
  const unitOptions = itemUnits.map((u: any) => ({ value: String(u.unit_id), label: unitLabel(u) }));
  const effUnitId = singleUnit ? String(singleUnit.unit_id) : discUnitId;
  const discUnit = itemUnits.find((u: any) => String(u.unit_id) === effUnitId);
  const discBase = discUnit ? (Number(discQty) || 0) * discUnit.conversion_factor : 0;

  const submit = async () => {
    if (hasDiscrepancy && (!effUnitId || !discQty)) { showToast("واحد و مقدار کسری/اضافه را وارد کنید", false); return; }
    setSubmitting(true);
    const res = await goodsReceiptAPI.confirmReceipt({
      id: row.id, received_date: receivedDate,
      has_discrepancy: hasDiscrepancy, discrepancy_direction: hasDiscrepancy ? direction : null,
      discrepancy_unit_id: hasDiscrepancy ? parseInt(effUnitId) : null, discrepancy_qty_units: hasDiscrepancy ? Number(discQty) : null,
      receipt_notes: receiptNotes, receipt_attachment: receiptAttachment.path,
    });
    setSubmitting(false);
    if (res.success) { showToast("رسید گدام تأیید شد"); onDone(hasDiscrepancy && direction === "overage" ? discBase : undefined); }
    else showToast(res.message || "خطا", false);
  };

  return (
    <Overlay onClose={onClose} T={T} dark={dark} wide>
      <div style={{ fontSize: 15, fontWeight: 800, color: T.text, marginBottom: 6 }}>تأیید رسید گدام</div>
      <div style={{ fontSize: 12, color: T.subtext, marginBottom: 18 }}>
        {fmt(row.quantity_units)} {row.unit_name} — 🏭 {row.warehouse_name} {row.truck_plate && `— 🚚 ${row.truck_plate}`}
      </div>

      <div style={{ marginBottom: 16 }}>
        <Lbl T={T}>تاریخ رسید</Lbl>
        <div style={{ maxWidth: 220 }}><PersianDatePicker value={receivedDate} onChange={setReceivedDate} /></div>
      </div>

      <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
        <button onClick={() => setHasDiscrepancy(false)} style={{ flex: 1, padding: "12px", borderRadius: 10, border: `2px solid ${!hasDiscrepancy ? "#10b981" : T.cardBorder}`, background: !hasDiscrepancy ? "rgba(16,185,129,0.08)" : "transparent", color: !hasDiscrepancy ? "#10b981" : T.subtext, cursor: "pointer", fontSize: 13, fontWeight: 800, fontFamily: "inherit" }}>
          ✓ کسری/اضافه ندارد
        </button>
        <button onClick={() => setHasDiscrepancy(true)} style={{ flex: 1, padding: "12px", borderRadius: 10, border: `2px solid ${hasDiscrepancy ? "#f59e0b" : T.cardBorder}`, background: hasDiscrepancy ? "rgba(245,158,11,0.08)" : "transparent", color: hasDiscrepancy ? "#f59e0b" : T.subtext, cursor: "pointer", fontSize: 13, fontWeight: 800, fontFamily: "inherit" }}>
          ⚠ کسری/اضافه دارد
        </button>
      </div>

      {hasDiscrepancy && (
        <div style={{ padding: 16, borderRadius: 12, background: dark ? "rgba(245,158,11,0.05)" : "#fffbeb", border: "1px solid rgba(245,158,11,0.2)", marginBottom: 16 }}>
          <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
            <button onClick={() => setDirection("shortage")} style={{ flex: 1, padding: "9px", borderRadius: 8, border: `1.5px solid ${direction === "shortage" ? "#ef4444" : T.cardBorder}`, background: direction === "shortage" ? "rgba(239,68,68,0.1)" : "transparent", color: direction === "shortage" ? "#ef4444" : T.subtext, cursor: "pointer", fontSize: 12.5, fontWeight: 700, fontFamily: "inherit" }}>کسری</button>
            <button onClick={() => setDirection("overage")} style={{ flex: 1, padding: "9px", borderRadius: 8, border: `1.5px solid ${direction === "overage" ? "#10b981" : T.cardBorder}`, background: direction === "overage" ? "rgba(16,185,129,0.1)" : "transparent", color: direction === "overage" ? "#10b981" : T.subtext, cursor: "pointer", fontSize: 12.5, fontWeight: 700, fontFamily: "inherit" }}>اضافه</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div><Lbl T={T}>واحد {!singleUnit && "*"}</Lbl>{singleUnit
              ? <div style={{ ...inputSt(T), display: "flex", alignItems: "center", fontWeight: 800, background: dark ? "rgba(16,185,129,0.08)" : "rgba(16,185,129,0.07)", borderColor: "rgba(16,185,129,0.35)", color: "#10b981" }}>{unitLabel(singleUnit)}</div>
              : <SearchableSelect options={unitOptions} value={discUnitId} onChange={setDiscUnitId} placeholder="انتخاب واحد (الزامی)" />}</div>
            <div>
              <Lbl T={T}>مقدار {direction === "shortage" ? "کسری" : "اضافه"}</Lbl>
              <input type="number" value={discQty} onChange={e => setDiscQty(e.target.value)} placeholder="0" style={inputSt(T)} />
              {discUnit && discQty && <div style={{ fontSize: 10.5, color: "#f59e0b", marginTop: 3 }}>= {fmt(discBase)} {item.unit}</div>}
            </div>
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
        <div><Lbl T={T}>توضیحات رسید</Lbl><input value={receiptNotes} onChange={e => setReceiptNotes(e.target.value)} placeholder="یادداشت..." style={inputSt(T)} /></div>
        <div>
          <Lbl T={T}>ضمیمه رسید</Lbl>
          <AttachmentField value={receiptAttachment.path} fileName={receiptAttachment.name} onChange={(path, name) => setReceiptAttachment({ path, name })} uploadFn={uploadAttachment} T={T} dark={dark} />
        </div>
      </div>

      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <button onClick={onClose} style={{ padding: "9px 18px", borderRadius: 9, border: `1px solid ${T.cardBorder}`, background: "transparent", color: T.text, cursor: "pointer", fontSize: 13, fontFamily: "inherit" }}>انصراف</button>
        <button onClick={submit} disabled={submitting} style={{ padding: "9px 22px", borderRadius: 9, border: "none", background: submitting ? "#94a3b8" : "linear-gradient(135deg,#10b981,#059669)", color: "#fff", cursor: submitting ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit" }}>
          {submitting ? "در حال ثبت..." : "تأیید رسید"}
        </button>
      </div>
    </Overlay>
  );
}

const Overlay = ({ children, onClose, T, dark, wide, xl }: any) => (
  <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(3px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000, padding: 20 }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
    <div style={{ background: dark ? "#1e293b" : "#fff", borderRadius: 20, padding: xl ? 40 : 30, maxWidth: xl ? 1040 : wide ? 520 : 380, minHeight: xl ? 640 : undefined, width: "100%", textAlign: wide ? "right" as const : "center" as const, boxShadow: "0 25px 60px rgba(0,0,0,0.4)", maxHeight: "88vh", overflow: "auto" }}>
      {children}
    </div>
  </div>
);

const Toast = ({ toast }: any) => toast.text ? (
  <div style={{ position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", zIndex: 9999, padding: "13px 26px", borderRadius: 12, background: toast.ok ? "#10b981" : "#ef4444", color: "#fff", fontWeight: 700, fontSize: 13, boxShadow: "0 6px 24px rgba(0,0,0,0.3)" }}>
    {toast.text}
  </div>
) : null;